import React from 'react'
import { Portal, useTheme } from '@mui/material'
import { Filter } from 'iconsax-react'

import Search from '@SonarCX/components/molecules/Table/Search'
import Button from '@SonarCX/components/atoms/Button2'
import SubtaskCheckbox from './SubtaskCheckbox'
import TasksFilterModal from '@SonarCX/components/molecules/TasksFilterModal'
import SideModal from '@SonarCX/components/atoms/SideModal'
import Views from './Views'
import Tooltip from '@SonarCX/components/atoms/Tooltip'
import ClientFilter from './ClientFilter'
import DueDateFilter from './DueDateFilter'
import PriorityFilter from './PriorityFilter'
import OwnerFilter from './OwnerFilter'
import LeadFilter from './LeadFilter'
import AssigneeFilter from './AssigneeFilter'
import CategoryFilter from './CategoryFilter'
import StatusFilter from './StatusFilter'
import TypeFilter from './TypeFilter'
import CreatedDateFilter from './CreatedDateFilter'
import CompletedStatusFilter from './CompletedStatusFilter'
import WatcherFilter from './WatcherFilter'
import FavoritesCheckbox from './FavoritesCheckbox'
import TeamFilter from './TeamFilter'

import { getURL } from '../helpers/parse'
import { useTaskFilters } from '@SonarCX/providers/TaskFiltersProvider'
import { useEnvironment } from '@SonarCX/providers/EnvironmentProvider'
import { useBreakpoint } from '@SonarCX/providers/BreakpointProvider'
import useStyles from './styles'

const Filters = ({
  onUpdate,
  tempSearch,
  setTempSearch,
  sort,
  customSort = [],
  disableSearch = false,
  disableIncludeSubtasks = false,
  disableFavorites = false,
  fieldValues = [],
  setFieldValues = _vals => {},
  hideViews = false,
  children = null
}) => {
  const theme = useTheme()

  const {
    state,
    count,
    clearAll,
    updateSearch,
    includeSubtasks,
    setIncludeSubtasks,
    saveFilter
  } = useTaskFilters()
  const { user } = useEnvironment()
  const { isSmall } = useBreakpoint()

  const [open, setOpen] = React.useState(false)
  const [showModal, setShowModal] = React.useState(false)
  const canFetch = React.useMemo(() => Boolean(user?.id), [user])
  const title = React.useMemo(
    () => `${count === 0 ? 'No' : count} Filter${count === 1 ? '' : 's'}`,
    [count]
  )
  const lastUrl = React.useRef('')

  const [checkFilters, setCheckFilters] = React.useState(false)

  const showSave = React.useMemo(() => {
    return count + fieldValues.length + tempSearch.length > 0
  }, [count, fieldValues, tempSearch])

  const classes = useStyles()

  const beginSave = React.useCallback(() => {
    setShowModal(true)
  }, [])

  const hideModal = React.useCallback(() => {
    setShowModal(false)
  }, [])

  // Updates search filter after a delay
  React.useEffect(() => {
    return updateSearch(tempSearch)
  }, [updateSearch, tempSearch])

  // Updates the url
  React.useEffect(() => {
    if (!canFetch) {
      return
    }

    // Calculates the url
    const url = getURL({
      ...state,
      includeSubtasks,
      userID: user.id,
      sort,
      customSort,
      fieldValues
    })

    // Only update when a change occurs
    if (lastUrl.current !== url) {
      onUpdate(url)
    }

    lastUrl.current = url
  }, [
    canFetch,
    state,
    includeSubtasks,
    user,
    sort,
    customSort,
    fieldValues,
    onUpdate
  ])

  const handleViewLoaded = React.useCallback(() => {
    // Pulse value to have menus check their expanded state
    setCheckFilters(true)
    setTimeout(() => setCheckFilters(false), 500)
  }, [])

  // Clear all filters, including search and custom fields
  const clearFilters = React.useCallback(() => {
    clearAll()
    setIncludeSubtasks(false)
    setTempSearch('')
    setFieldValues([])
  }, [clearAll, setIncludeSubtasks, setTempSearch, setFieldValues])

  return (
    <>
      <div onClick={() => setOpen(old => !old)} className={classes.filter}>
        <Tooltip title='Filters' arrow>
          <div
            style={{ display: 'flex', alignItems: 'center', height: '100%' }}
          >
            <Filter variant='Outline' size={theme.size.base} className='icon' />
          </div>
        </Tooltip>

        <div className='text'>{title}</div>

        {children && <span onClick={e => e.stopPropagation()}>{children}</span>}

        {isSmall && !disableIncludeSubtasks && <SubtaskCheckbox />}
      </div>
      {!isSmall && !disableIncludeSubtasks && <SubtaskCheckbox />}
      {!isSmall && !disableFavorites && <FavoritesCheckbox />}

      {!disableSearch && (
        <Search
          searchVal={tempSearch}
          onSearch={filter => setTempSearch(filter.trim().toLocaleLowerCase())}
          fullWidth
        />
      )}
      {open && (
        <Portal>
          <SideModal
            title={title}
            onClose={() => setOpen(false)}
            sidemodalKey='task-filters'
            rootClass={classes.sideModal}
          >
            {!hideViews && (
              <Views
                fieldValues={fieldValues}
                setFieldValues={setFieldValues}
                setTempSearch={setTempSearch}
                onLoad={handleViewLoaded}
              />
            )}

            <ClientFilter checkFilters={checkFilters} hideViews={hideViews} />

            <DueDateFilter checkFilters={checkFilters} />

            <PriorityFilter checkFilters={checkFilters} />

            <OwnerFilter checkFilters={checkFilters} />

            <LeadFilter checkFilters={checkFilters} />

            <AssigneeFilter checkFilters={checkFilters} />

            <CategoryFilter checkFilters={checkFilters} />

            <StatusFilter checkFilters={checkFilters} />

            <TypeFilter checkFilters={checkFilters} />

            <CreatedDateFilter checkFilters={checkFilters} />

            <CompletedStatusFilter checkFilters={checkFilters} />

            <WatcherFilter checkFilters={checkFilters} />

            <TeamFilter checkFilters={checkFilters} />

            <div className={classes.buttonSection}>
              <Button onClick={clearFilters} className={classes.button}>
                CLEAR ALL
              </Button>

              {showSave && (
                <Button onClick={beginSave} className={classes.button}>
                  SAVE FILTER
                </Button>
              )}
            </div>
          </SideModal>
        </Portal>
      )}
      {showModal && (
        <TasksFilterModal
          selected={state}
          selectedFields={fieldValues}
          onClose={hideModal}
          onSave={name =>
            saveFilter({ name, url: lastUrl.current, onFinish: hideModal })
          }
        />
      )}
    </>
  )
}

export default Filters
